"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="233704" className="flex gap-2">
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  const items = menuItems && menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "RECIPES",
    href: "/AutomationRecipesPage"
  }, {
    id: "2",
    label: "THE METHOD",
    href: "/MozaMethodPage"
  }, {
    id: "3",
    label: "CONTACT",
    href: "/GetInTouchPage"
  }];
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-black/5 data-[state=open]:bg-black/5 text-foreground font-default text-[12px] tracking-widest uppercase")}>
              <Link to={item.href || "#"}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  const items = menuItems && menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "RECIPES",
    href: "/AutomationRecipesPage"
  }, {
    id: "2",
    label: "THE METHOD",
    href: "/MozaMethodPage"
  }, {
    id: "3",
    label: "CONTACT",
    href: "/GetInTouchPage"
  }];
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-black/5">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-background/95 backdrop-blur-2xl border-l border-border/50 sm:max-w-sm">
        <div className="flex flex-col gap-6 mt-12">
          {loading ? <div className="flex flex-col gap-4">
              <div className="h-8 w-32 bg-muted/50 animate-pulse rounded-md" />
              <div className="h-8 w-32 bg-muted/50 animate-pulse rounded-md" />
            </div> : <div className="flex flex-col gap-4">
              {items.map((item, i) => <div key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="text-sm font-default tracking-widest uppercase text-foreground/80 hover:text-primary transition-colors block py-2" onClick={() => setIsOpen(false)}>
                    {item.label}
                  </Link>
                </div>)}
            </div>}
          <div className="pt-6 border-t border-border/50">
            <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full font-default text-[12px] tracking-widest uppercase">
              START AUTOMATING
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-[20px] shadow-sm" : "border-none")}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex items-center z-50 shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/moza_casa_logo.png" alt="moza casa" className="h-6 w-auto" />
        </Link>

        {}
        <div className="hidden md:flex flex-1 justify-end mr-8">
          <MenuProvider menu_id="41">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4 shrink-0">
          <Button variant="outline" className="hidden md:inline-flex border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-full font-default text-[12px] tracking-widest uppercase px-6">
            START AUTOMATING
          </Button>

          {}
          <div className="md:hidden">
            <MenuProvider menu_id="41">
              <MobileMenuConsumer />
            </MenuProvider>
          </div>
        </div>
      </div>
    </section>;
}